import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosInstance } from 'axios';
import { pushNotification } from '../../state/slices/notificationSlice';
import { NotificationType } from '../../components/notifications/notification-component';
import { useDispatch } from 'react-redux';
import { Reviewer } from './lawEnforcementOfficersSlice';

export interface Agency {
  code: string;
  short_description: string;
  description: string;
  districts: District[];
}

export interface District {
  code: string;
  short_description: string;
  description: string;
  agency_code: string;
}

export interface lawEnforcementConfigurationState {
  configuration: {
    agencies?: Agency[];
  };
  isLoading: boolean;
  error: string | null;
}

const initialState: lawEnforcementConfigurationState = {
  configuration: {
    agencies: [],
  },
  isLoading: false,
  error: null,
};

export const fetchAllLawEnforcenmentConfigurations = createAsyncThunk(
  'lawEnforcementConfig/fetchAllLawEnforcenmentConfigurations',
  async (
    { csrService }: { csrService: AxiosInstance },
    { rejectWithValue },
  ) => {
    try {
      const agenciesResponse = await csrService.get(
        '/api/v2/law-enforcement/agencies',
      );
      const agenciesWithDistricts = await Promise.all(
        agenciesResponse.data.map(async (agency: Agency) => {
          const agencyCopy = { ...agency, districts: [] };

          try {
            const allDistricts = await csrService.get(
              `/api/v2/law-enforcement/districts?agency=${agency.code}`,
            );

            if (allDistricts) {
              agencyCopy.districts = allDistricts.data.map(
                (district: any) => ({
                  code: district.code,
                  short_description: district.short_description,
                  description: district.description,
                  agency_code: agency.code
                }),
              );
            }
          } catch (error) {
            console.error(
              'Error fetching districts for agency:',
              agency.code,
              error,
            );
          }

          return agencyCopy;
        }),
      );

      return {
        agencies: agenciesWithDistricts,
      };
    } catch (error: any) {
      const dispatch = useDispatch();
      dispatch(
        pushNotification({
          body: 'Failed to fetch configuration',
          notificationType: NotificationType.importantGenericError,
        }),
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const fetchLawEnforcementConfiguration = createAsyncThunk(
  'lawEnforcementConfig/fetchLawEnforcementConfiguration',
  async (
    { agency, csrService }: { agency: string; csrService: AxiosInstance },
    { rejectWithValue },
  ) => {
    try {
      const [agencies] = await Promise.all([
        csrService.get('/api/v2/law-enforcement/agencies'),
      ]);

      const foundAgency = agencies.data.find((x: Agency) => x.code == agency);

      if (foundAgency) {
        const [districts] = await Promise.all([
          csrService.get(`/api/v2/law-enforcement/districts?agency=${agency}`),
        ]);

        foundAgency.districts = districts.data.map((district: any) => ({
          code: district.code,
          short_description: district.short_description,
          description: district.description,
          agency_code: agency
        }));
      }

      return {
        agency: foundAgency,
      };
    } catch (error: any) {
      const dispatch = useDispatch();
      dispatch(
        pushNotification({
          body: 'Failed to fetch configuration',
          notificationType: NotificationType.importantGenericError,
        }),
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const lawEnforcementConfigSlice = createSlice({
  name: 'lawEnforcementConfig',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLawEnforcementConfiguration.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchLawEnforcementConfiguration.fulfilled,
        (state, action: PayloadAction<any>) => {
          if (action.payload.agency) {
            const exists = state.configuration.agencies!.some(
              (item) => item.code === action.payload.agency.code,
            );
            if (!exists) {
              state.configuration.agencies!.push(action.payload.agency);
            }
          }

          state.isLoading = false;
          state.error = null;
        },
      )
      .addCase(
        fetchLawEnforcementConfiguration.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload;
        },
      )
      .addCase(fetchAllLawEnforcenmentConfigurations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchAllLawEnforcenmentConfigurations.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.configuration.agencies = action.payload.agencies;
          state.isLoading = false;
          state.error = null;
        },
      )
      .addCase(
        fetchAllLawEnforcenmentConfigurations.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload;
        },
      );
  },
});

export default lawEnforcementConfigSlice.reducer;
